/**
 * Disponibilidade consolidada (Task 11 / D-11.5).
 *
 * Regra do brief, sem exceção: o que a Plataforma exibe como disponível é
 * **estoque vendável − reservas ativas**, calculado por
 * `consolidatedAvailability` de `packages/catalog` (a MESMA regra da função SQL
 * `consolidated_availability` da migration 0019). Estoque bruto do operacional
 * nunca aparece como disponibilidade — é por isso que preço/estoque defasado
 * não gera venda indevida.
 *
 * Duas ausências diferentes, nunca confundidas:
 * - **sem oferta vendável** (`sellableStock: null`): não há o que vender, a
 *   disponibilidade é 0 e a tela diz o motivo;
 * - **fonte de reservas não publicada** (`reservationsAvailable: false`): a
 *   disponibilidade é exibida sem subtração e a tela declara isso.
 *
 * Puro e sem I/O.
 */
import { consolidatedAvailability } from "../../../../packages/catalog/src/index.js";
import type { PlatformCatalogProduct, StockReservation } from "../api/types.js";

/**
 * Disponibilidade consolidada a partir da qual um item é crítico (default
 * operacional D-10.7: <= 3 unidades vendáveis).
 *
 * Mora AQUI, e não no módulo de KPIs, porque disponibilidade é regra de
 * catálogo: o KPI de estoque crítico do dashboard reexporta esta constante
 * (`dashboard/kpis.ts`) para que o card, a tela de estoque e a tela de
 * disponibilidade usem exatamente o mesmo corte — um número só.
 */
export const CRITICAL_STOCK_THRESHOLD = 3;

export interface AvailabilityBreakdown {
  productId: string;
  sku: string;
  /** Estoque da oferta vendável vigente; `null` = nenhuma oferta vendável. */
  sellableStock: number | null;
  activeReservations: number;
  /** `estoque vendável − reservas ativas`, nunca negativo. */
  consolidated: number;
  critical: boolean;
}

/** Somente reservas `active` descontam: `released`/`consumed` não bloqueiam venda. */
export function activeReservations(reservations: StockReservation[]): number {
  return reservations
    .filter((reservation) => reservation.status === "active")
    .reduce((total, reservation) => total + reservation.quantity, 0);
}

export function availabilityOfProduct(
  product: PlatformCatalogProduct,
  threshold: number = CRITICAL_STOCK_THRESHOLD
): AvailabilityBreakdown {
  const sellableStock = product.sellableOffer ? product.sellableOffer.stock : null;
  const reserved = activeReservations(product.reservations);
  const consolidated = consolidatedAvailability(sellableStock, reserved);
  return {
    productId: product.productId,
    sku: product.operational.sku,
    sellableStock,
    activeReservations: reserved,
    consolidated,
    // Sem oferta vendável o item não é "estoque crítico": é indisponível, e a
    // tela mostra os dois estados separadamente.
    critical: sellableStock !== null && consolidated <= threshold
  };
}

export function availabilityBreakdowns(
  products: PlatformCatalogProduct[],
  threshold: number = CRITICAL_STOCK_THRESHOLD
): AvailabilityBreakdown[] {
  return products.map((product) => availabilityOfProduct(product, threshold));
}

/**
 * Índice `productId → reservas ativas`, consumido pelo cálculo do KPI de
 * estoque crítico (`dashboard/kpis.ts`). Existe para que o dashboard e a tela
 * de disponibilidade usem a MESMA subtração — um número só, não dois.
 */
export function activeReservationIndex(products: PlatformCatalogProduct[] | null): Map<string, number> {
  const index = new Map<string, number>();
  if (!products) return index;
  for (const product of products) {
    index.set(product.productId, activeReservations(product.reservations));
  }
  return index;
}

/** Preço vigente da oferta vendável; `null` quando não há oferta. */
export function currentPriceCentsOf(product: PlatformCatalogProduct): number | null {
  return product.sellableOffer ? product.sellableOffer.priceCents : null;
}

/** Menor preço B2B ativo do produto; `null` quando não há tabela aplicável. */
export function lowestB2bPriceCentsOf(product: PlatformCatalogProduct): number | null {
  const prices = product.b2bPriceTables
    .filter((table) => table.active)
    .flatMap((table) => table.entries.map((entry) => entry.priceCents));
  if (!prices.length) return null;
  return Math.min(...prices);
}
